/**
 * Search Suggest Render Module
 * Handles rendering search suggestion dropdown
 */

import { translator } from "../../translation/translator";
import { SpinnerRender } from "../../util/spinner/spinner-render";
import type { SearchSuggestItem } from "./search-suggest-api";
import { Roles } from "../../constants/roles";
import { searchSuggestForm } from "./search-suggest-form";
import { SearchSuggestListener } from "./search-suggest-listener";
import { CommonUtils } from "../../util/common-utils";
import { UISymbols } from "../../constants/ui-symbols";

class SearchSuggestRender {
    public dropdownClass: string;

    public itemClass: string;

    public activeClass: string;

    public typeIconClasses: Record<string, string>;

    /**
     * Dropdowns are portaled to document.body (see createDropdown()) rather
     * than nested in the page's DOM near the input, so these track the
     * input<->dropdown relationship that DOM nesting used to provide for
     * free — createDropdown()'s idempotency check, and positionDropdown()'s
     * anchor rect.
     */
    private dropdownByInput = new WeakMap<HTMLElement, HTMLElement>();

    private inputByDropdown = new WeakMap<HTMLElement, HTMLElement>();

    public constructor() {
        this.dropdownClass = "search-suggest-dropdown";
        this.itemClass = "search-suggest-item";
        this.activeClass = "active";
        this.typeIconClasses = {
            brand: "bi-tag",
            product: "bi-box-seam",
            category: "bi-folder2-open",
            user: "bi-person",
            post_title: "bi-journal-text",
            hashtag: "bi-hash",
            search_type: "bi-search",
        };
    }

    public getTypeIconHtml(type: string): string {
        const iconClass = this.typeIconClasses[type] || "bi-dot";
        return `<i class="bi ${iconClass}" aria-hidden="true"></i>`;
    }

    /**
     * Price + rating chips for product suggestions — the underlying data
     * (metadata.price/rating/review_count) was already generated by
     * search:generate-suggests but never rendered, so this was previously
     * indistinguishable from a plain text-only suggestion.
     */
    public buildProductPayloadHtml(item: SearchSuggestItem): string {
        const chips: string[] = [];

        const priceRaw = item.metadata?.price;
        const price = typeof priceRaw === "number" ? priceRaw : Number(priceRaw);
        if (Number.isFinite(price) && price > 0) {
            chips.push(`<span class="suggest-price">฿${price.toLocaleString()}</span>`);
        }

        const ratingRaw = item.metadata?.rating;
        const rating = typeof ratingRaw === "number" ? ratingRaw : Number(ratingRaw);
        if (Number.isFinite(rating) && rating > 0) {
            const reviewCountRaw = item.metadata?.review_count;
            const reviewCount = typeof reviewCountRaw === "number" ? reviewCountRaw : Number(reviewCountRaw);
            const reviewCountHtml = Number.isFinite(reviewCount) && reviewCount > 0
                ? ` <span class="suggest-review-count">(${reviewCount.toLocaleString()})</span>`
                : "";
            chips.push(
                `<span class="suggest-rating">` +
                    `<span class="suggest-rating__star" aria-hidden="true">${UISymbols.STAR_FILLED}</span>` +
                    `${CommonUtils.formatAverageRating(rating, 1)}${reviewCountHtml}` +
                `</span>`,
            );
        }

        return chips.join("");
    }

    /**
     * Portaled to document.body — position: fixed alone wasn't reliably
     * enough to render above page content once the dropdown lived inside a
     * complex ancestor tree (e.g. a board-card's image slider, which sets
     * will-change: transform on itself for its own transition performance;
     * see ISS-04-103). Being an actual sibling of <body>'s other top-level
     * content sidesteps any such ancestor's stacking context entirely,
     * rather than relying on z-index alone to win against it.
     */
    public createDropdown(inputElement: HTMLElement): HTMLElement {
        const existing = this.dropdownByInput.get(inputElement);
        if (existing) return existing;

        const dropdown = document.createElement("div");
        dropdown.className = this.dropdownClass;
        dropdown.setAttribute("data-role", Roles.SEARCH_SUGGEST_DROPDOWN);
        dropdown.style.display = "none";

        document.body.appendChild(dropdown);
        this.dropdownByInput.set(inputElement, dropdown);
        this.inputByDropdown.set(dropdown, inputElement);

        return dropdown;
    }

    public renderSuggestions(dropdown: HTMLElement, suggestions: SearchSuggestItem[], query: string): void {
        if (!suggestions || suggestions.length === 0) {
            this.hideDropdown(dropdown);
            return;
        }

        const items = suggestions
            .map((item, index) => {
                const highlightedText = this.highlightMatch(String(item.text || item.value || ""), query);
                const type = String(item.type || "");
                const iconHtml = this.getTypeIconHtml(type);
                const typeLabel = this.getTypeLabel(type, item.metadata?.board_id);
                const description = String(item.metadata?.description || "").trim();
                const imageUrl = String(item.metadata?.image_url || "").trim();
                const payloadHtml = type === "product" ? this.buildProductPayloadHtml(item) : "";
                const brandName = type === "product" ? String(item.metadata?.brand_name || "").trim() : "";

                return `
                <div class="${this.itemClass}" data-role="${Roles.SEARCH_SUGGEST_ITEM}" data-index="${index}" data-value="${this.escapeHtml(String(item.value ?? ""))}"
                     data-type="${this.escapeHtml(String(item.type || ""))}" data-output-field="${this.escapeHtml(String(item.output_field || ""))}"
                     data-output-value="${this.escapeHtml(String(item.output_value || ""))}">
                    ${imageUrl ? `<img src="${this.escapeHtml(imageUrl)}" class="suggest-image" alt="" referrerpolicy="no-referrer">` : `<span class="suggest-icon">${iconHtml}</span>`}
                    <div class="suggest-content">
                        <div class="suggest-text" data-role="${Roles.SEARCH_SUGGEST_TEXT}">${highlightedText}</div>
                        ${brandName !== "" ? `<div class="suggest-subtitle">${this.escapeHtml(brandName)}</div>` : ""}
                        <div class="suggest-meta">
                            <span class="suggest-type">${typeLabel}</span>
                            ${payloadHtml}
                            ${description !== "" ? `<span class="suggest-desc">${this.escapeHtml(description)}</span>` : ""}
                        </div>
                    </div>
                </div>
            `;
            })
            .join("");

        dropdown.innerHTML = items;
        this.bindImageFallbacks(dropdown);
        this.showDropdown(dropdown);
    }

    /**
     * metadata.image_url being present only means the source data had a
     * value for it — the file itself can still 404 (broken/missing upload,
     * stale URL, ...). Without this, a failed <img> shows the browser's own
     * broken-image glyph instead of falling back to the same type icon a
     * missing image_url already renders.
     */
    private bindImageFallbacks(dropdown: HTMLElement): void {
        searchSuggestForm.getSuggestImages(dropdown).forEach((img) => {
            const item = searchSuggestForm.getClosestItem(img);
            const type = item?.dataset.type || "";

            SearchSuggestListener.bindImageFallback(img, () => {
                const icon = document.createElement("span");
                icon.className = "suggest-icon";
                icon.innerHTML = this.getTypeIconHtml(type);
                img.replaceWith(icon);
            });
        });
    }

    public renderLoading(dropdown: HTMLElement): void {
        const spinner = SpinnerRender.createInlineSpinner({ size: "normal" });
        dropdown.innerHTML = `
            <div class="search-suggest-loading">
                ${spinner.outerHTML}
                <div class="loading-text"></div>
            </div>
        `;
        translator.updateUITranslations();
        this.showDropdown(dropdown);
    }

    public renderError(dropdown: HTMLElement): void {
        dropdown.innerHTML = '<div class="search-suggest-error" data-translate="error_occurred">เกิดข้อผิดพลาด</div>';
        translator.updateUITranslations();
        this.showDropdown(dropdown);
    }

    public highlightMatch(text: string, query: string): string {
        if (!query || !text) return this.escapeHtml(text);

        const escapedText = this.escapeHtml(text);
        const escapedQuery = this.escapeHtml(query);
        const regex = new RegExp(`(${escapedQuery})`, "gi");

        return escapedText.replace(regex, "<mark>$1</mark>");
    }

    /**
     * post_title suggestions are labeled by their actual board (board_beauty_news,
     * board_editorial_reviews, ... — dict-navigation.ts) rather than the generic
     * search_type_post_title ("Post"), so the badge reflects which board a
     * result actually came from instead of a fixed label.
     */
    public getTypeLabel(type: string, boardId?: unknown): string {
        if (type === "post_title" && typeof boardId === "string" && boardId !== "") {
            const boardLabel = translator.translate(`board_${boardId}`);
            if (boardLabel) return boardLabel;
        }

        const translationKey = `search_type_${type}`;
        return translator.translate(translationKey) || type;
    }

    public setActiveItem(dropdown: HTMLElement, index: number): void {
        const items = searchSuggestForm.getItems(dropdown);

        items.forEach((item, i) => {
            if (i === index) {
                item.classList.add(this.activeClass);
                item.scrollIntoView({ block: "nearest", behavior: "smooth" });
            } else {
                item.classList.remove(this.activeClass);
            }
        });
    }

    public getActiveItem(dropdown: HTMLElement): HTMLElement | null {
        return searchSuggestForm.getItems(dropdown).find((item) => item.classList.contains(this.activeClass)) || null;
    }

    public hideDropdown(dropdown: HTMLElement): void {
        dropdown.style.display = "none";
    }

    public showDropdown(dropdown: HTMLElement): void {
        this.positionDropdown(dropdown);
        dropdown.style.display = "block";
    }

    /**
     * Positions the (now document.body-portaled — see createDropdown()) dropdown
     * from its input's current on-screen rect, computed fresh each time the
     * dropdown opens rather than tracked continuously — a reasonable
     * trade-off for a popover that's only open while typing.
     */
    private positionDropdown(dropdown: HTMLElement): void {
        const anchor = this.inputByDropdown.get(dropdown);
        if (!(anchor instanceof HTMLElement)) return;

        const rect = anchor.getBoundingClientRect();
        const minWidth = 380;
        const maxWidth = 480;
        const margin = 8;
        // Widening to at least minWidth helps a narrow (e.g. sidebar) input,
        // but a wide input (e.g. a full-width navbar search bar) shouldn't
        // stretch the dropdown to match — capped at maxWidth either way.
        // Also capped at the viewport's own available width: on a screen
        // narrower than minWidth (e.g. a 360px phone), forcing 380px here
        // pushed the dropdown's right edge past the screen edge, cutting it
        // off — the viewport cap takes priority over the "at least minWidth"
        // preference once the two conflict.
        const availableWidth = window.innerWidth - margin * 2;
        const width = Math.min(Math.max(rect.width, minWidth), maxWidth, availableWidth);
        const left = Math.min(rect.left, Math.max(margin, window.innerWidth - width - margin));
        const availableHeight = window.innerHeight - rect.bottom - 16;

        dropdown.style.position = "fixed";
        dropdown.style.top = `${rect.bottom + 4}px`;
        dropdown.style.left = `${left}px`;
        dropdown.style.right = "auto";
        dropdown.style.width = `${width}px`;
        // The stylesheet's min-width: 100% (a sane default while this was
        // position: absolute inside a narrow local container) resolves
        // against the *viewport* now that the dropdown is position: fixed
        // directly under <body> — silently stretching it edge-to-edge no
        // matter what `width` above says, since min-width wins when larger.
        // Must be pinned back here explicitly.
        dropdown.style.minWidth = `${width}px`;
        dropdown.style.maxHeight = `${Math.max(200, Math.min(600, availableHeight))}px`;
    }

    public escapeHtml(text: string): string {
        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
    }
}

export { SearchSuggestRender };

export const searchSuggestRender = new SearchSuggestRender();
